import { useEffect, useState } from 'react'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { eMoneySettingsDefaultValues, EMoneySettingsFormValues } from './model'
import { Controller, useForm } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Button } from '../../../../../components/Button/Button'

const E_MONEY_CONTACT_SYNC_MAX_OBJECT_COUNT = 5000

export const EMoneyContactSyncSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'emoney',
    direction: 'inbound',
    object: 'contact_operation_hashed',
    scheduling: '*/30 * * * *',
    payload: {
      maxObjectCount: E_MONEY_CONTACT_SYNC_MAX_OBJECT_COUNT,
    },
  } as CheckpointRequest

  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()

  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const form = useForm<EMoneySettingsFormValues>({ defaultValues: eMoneySettingsDefaultValues })

  useEffect(() => {
    if (isGetCheckpointFetching || !checkpoint) {
      return
    }
    form.setValue('inbound.contact.autoSync', checkpoint.data?.active || false)
  }, [isGetCheckpointFetching, checkpoint, form])

  const onSubmit = async (data: EMoneySettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (!checkpoint?.data) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest)
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  if (!isUpdating && isGetCheckpointFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and eMoney.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isGetCheckpointFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isCreateCheckpointLoading || isUpdateCheckpointLoading}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
